import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Form,
  PlusIcon,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
  SelectFormField,
} from "@theprelude/ds";
import {
  FieldArrayWithId,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { z } from "zod";
import { Filter, filterSchema, TopLevelFilters } from "@/lib/filter";
import { isValidRegex } from "@/lib/is-valid-regex";
import { getOdataId, odataOperatorSchema } from "@/lib/odata";
import { AdditionalFilter, isDurationId } from "./additional-filters";
import {
  DEFAULT_PARSE,
  FilterFormField,
  formFilterValueSchema,
  getDefaultCast,
} from "./filter-form";

const categorySchema = z.enum(["endpoints", "users", "inboxes"]);
export type Category = z.infer<typeof categorySchema>;

const formFilterSchema = filterSchema
  .extend({
    category: z.union([categorySchema, z.literal("")]),
    value: z.string().nonempty(),
    operator: z.union([odataOperatorSchema, z.literal("")]),
    ors: z.array(formFilterValueSchema).optional(),
  })
  .refine(
    (input) =>
      (input.operator !== "matches" && input.operator !== "does not match") ||
      isValidRegex(input.value),
    { message: "Invalid regular expression", path: ["value"] },
  )
  .refine((input) => !isDurationId(input.id) || isValidDuration(input.value), {
    message: "Duration must be at least 1 day",
    path: ["value"],
  });

const filterFormSchema = z.object({
  filters: z.array(formFilterSchema),
});

type FormFilter = z.infer<typeof formFilterSchema>;
type FilterFormData = z.infer<typeof filterFormSchema>;

const DEFAULT_FILTERS: FormFilter[] = [
  { id: "", operator: "", value: "", category: "", ors: [] },
];

type CategorizedAdditionalFilter = AdditionalFilter & {
  category: Category;
};

type CategorizedFilter = Filter & {
  category: Category;
};

const isValidDuration = (duration?: string) => {
  if (!duration) {
    return true;
  }
  return Boolean(Number.parseInt(duration, 10));
};

function convertFiltersToFormFilters(
  currentFilters: CategorizedFilter[],
  filters: CategorizedAdditionalFilter[],
): FormFilter[] {
  return currentFilters.map((f) => {
    const id = getOdataId(f);
    const formField = filters.find(
      (c) => c.id === id && c.category === f.category,
    );
    const parse = formField?.parse ?? DEFAULT_PARSE;
    const formFilter = parse({ ...f, id });
    return { ...formFilter, category: f.category };
  });
}

function normalizedFilterToFilter(filter: CategorizedFilter): Filter {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { category, ...rest } = filter;
  return rest;
}

function convertFormFiltersToFilters(
  currentFilters: FormFilter[],
  filters: CategorizedAdditionalFilter[],
): TopLevelFilters {
  const normalizedFilters = currentFilters
    .map((f) => {
      const formField = filters.find(
        (c) => c.id === f.id && c.category === f.category,
      );
      const cast = formField?.cast ?? getDefaultCast(formField?.type);

      const filter = cast({ ...f, ...formField?.odata });
      return { ...filter, category: f.category };
    })
    .filter((f): f is Filter & { category: Category } => !!f.category);

  const grouped = Object.groupBy(normalizedFilters, (f) => f.category ?? "");

  return {
    endpoints: grouped.endpoints?.map(normalizedFilterToFilter),
    users: grouped.users?.map(normalizedFilterToFilter),
    inboxes: grouped.inboxes?.map(normalizedFilterToFilter),
  };
}

export function TopLevelFilterForm({
  availableFilters,
  onSubmit,
  onClose,
  onClear,
  defaultFilters,
}: {
  availableFilters: Record<Category, AdditionalFilter[]>;
  onSubmit: (data: TopLevelFilters) => void;
  onClear: () => void;
  onClose: () => void;
  defaultFilters: TopLevelFilters;
}) {
  const categorizedFilters: CategorizedAdditionalFilter[] = Object.entries(
    availableFilters,
  ).flatMap(([category, filters]) =>
    filters.map((f) => ({ ...f, category: category as Category })),
  );

  const categorizedCurrentFilters: CategorizedFilter[] = Object.entries(
    defaultFilters,
  ).flatMap(([category, filters]) =>
    filters.map((f) => ({ ...f, category: category as Category })),
  );

  const form = useForm<FilterFormData>({
    defaultValues: {
      filters:
        categorizedCurrentFilters.length > 0
          ? convertFiltersToFormFilters(
              categorizedCurrentFilters,
              categorizedFilters,
            )
          : DEFAULT_FILTERS,
    },
    resolver: zodResolver(filterFormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "filters",
  });

  async function submit(data: FilterFormData) {
    onSubmit(convertFormFiltersToFilters(data.filters, categorizedFilters));
    onClose();
  }

  return (
    <ScrollAreaRoot>
      <ScrollAreaViewport className="max-h-[500px]">
        <Form
          {...form}
          className="flex max-w-[80vw] flex-col justify-between gap-2 overflow-y-scroll p-4"
          onSubmitValid={submit}
        >
          {fields.map((field, index) => (
            <CategorizedFilterFormField
              key={field.id}
              field={field}
              index={index}
              onRemove={remove}
              categorizedFilters={categorizedFilters}
            />
          ))}
          <div className="mt-1 flex gap-2">
            <div className="flex gap-2">
              <Button
                icon={<PlusIcon />}
                intent="secondary"
                type="button"
                onClick={() =>
                  append({
                    category: "",
                    id: "",
                    operator: "",
                    value: "",
                    ors: [],
                  })
                }
              >
                Add filter
              </Button>
              <Button intent="primary">Apply</Button>
            </div>
            <div className="flex gap-2">
              <Button
                intent="text-link"
                type="button"
                onClick={() => {
                  onClear();
                  onClose();
                }}
              >
                Clear all
              </Button>
            </div>
          </div>
        </Form>
      </ScrollAreaViewport>
      <ScrollBar orientation="vertical" />
    </ScrollAreaRoot>
  );
}

interface CategorizedFilterFormFieldProps {
  field: FieldArrayWithId<FilterFormData>;
  index: number;
  onRemove: (index: number) => void;
  categorizedFilters: CategorizedAdditionalFilter[];
}

const CategorizedFilterFormField = ({
  field,
  index,
  onRemove,
  categorizedFilters,
}: CategorizedFilterFormFieldProps) => {
  const { register, setValue, watch, getValues } =
    useFormContext<FilterFormData>();

  const filters = getValues("filters");
  const currentFilter = filters[index];

  return (
    <div
      key={field.id}
      className="grid w-full grid-cols-1 items-start gap-2 md:grid-cols-4"
    >
      <div className="col-span-1 flex items-end gap-4">
        {index !== 0 && (
          <div className="col-span-1 mb-1.5 w-max rounded-md border border-stroke-line px-2 py-1 text-xs text-secondary-foreground font-medium">
            AND
          </div>
        )}
        <SelectFormField
          {...register(`filters.${index}.category`, {
            onChange: (event) => {
              setValue(
                `filters.${index}.category`,
                event.target.value as Category,
              );
              setValue(`filters.${index}.id`, "");
              setValue(`filters.${index}.operator`, "");
              setValue(`filters.${index}.value`, "");
              setValue(`filters.${index}.ors`, []);
            },
          })}
          label="Type"
          size="sm"
          required
          options={[
            { label: "Select a type", value: "", disabled: true },
            { label: "Endpoints", value: "endpoints" },
            { label: "Users", value: "users" },
            { label: "Inboxes", value: "inboxes" },
          ]}
          className="w-full"
        />
      </div>
      {currentFilter && (
        <FilterFormField
          field={currentFilter}
          index={index}
          availableFilters={categorizedFilters.filter(
            (f) => f.category === watch(`filters.${index}.category`),
          )}
          onRemove={onRemove}
          className="col-span-3 grid grid-cols-3"
        />
      )}
    </div>
  );
};
